import {
  FC,
  useEffect,
  useRef,
} from "react";

import { EdtechPlayerManager } from "@repo/edtech-player-manager";
import {
  EdtechProvider,
  IS_WEBCOM,
  Theme,
  useThemeSwitcher,
  useToken,
} from "@repo/edtech-shared";
import { Loader } from "@repo/edtech-ui-kit";
import { TypographyProvider } from "@repo/wb-ui-kit";
import clsx from "clsx";

import {
  getCourseIds,
  useMainData,
} from "../lib";
import {
  AnalyticsProvider,
  IdProvider,
  LessonStatus,
  MainProvider,
  SnackbarProvider,
} from "../model";
import { getConfig } from "../origins";
import { CourseCompletion, TelegramWarningModal } from "./components";

import "./../../../../../packages/components/wb-ui-kit/styles/index.scss";
import "./styles/index.scss";
import st from "./EdtechCourseCompletion.module.scss";

const {
  publicationId: urlPublicationId,
  unitId: urlUnitId,
  link: urlLink,
} = getCourseIds();

type Props = {
  token?: string,
  publicationId?: string,
  unitId?: string,
  backButtonPath?: string,
  id?: string,
  theme?: Theme,
  inheritBackground?: boolean,
  noIndent?: boolean,
  hideRating?: boolean,
  inheritTheme?: string,
  isAutomaticFinishLesson?: boolean,
};

export const EdtechCourseCompletion: FC<Props> = ({
  token,
  publicationId: propPublicationId,
  unitId: propUnitId,
  backButtonPath,
  theme = Theme.Light,
  inheritBackground,
  noIndent,
  hideRating = false,
  inheritTheme,
  isAutomaticFinishLesson = true,
}) => {

  const {
    videoWithoutBorderRadius,
    excludeNotALessonFromProgress,
  } = getConfig();
  // TODO create theme with id
  useThemeSwitcher({
    inheritTheme,
    initialTheme: theme,
  });
  useToken(token);

  useEffect(() => {
    if (IS_WEBCOM) return;
    document.documentElement.style.setProperty("--panel-bg", "var(--bg-basic-1)");
  }, [ theme ]);

  const publicationId = propPublicationId ?? urlPublicationId ?? "";
  const unitId = propUnitId ?? urlUnitId ?? "";

  const analyticEventsCache = useRef(new Set<string>());

  const {
    data: courseData,
    isLoading,
    isFirstLoading,
    onlyLessonsList,
    library,
    setLibrary,
    rootNodeIds,
    unitsChildren,
    passedLessonsCount,
    progressLessonsCount,
    progressCompletedLessonsCount,
    isAllRootElementsFinished,
  } = useMainData({
    publicationId,
    unitId,
    link: urlLink ?? undefined,
  });

  const courseProgressLessonsCount = excludeNotALessonFromProgress ?
    progressLessonsCount :
    onlyLessonsList.length;
  const courseProgressCompletedLessonsCount = excludeNotALessonFromProgress ?
    progressCompletedLessonsCount :
    passedLessonsCount;

  const isPublicCourse = courseData?.available === true;
  const isCourseClosed = courseData?.available === false;
  const lacksTokenInWC = IS_WEBCOM && !token;

  return (
    <EdtechProvider name="course-completion">
      <section
        className={
          clsx(
            st.app,
            inheritBackground && st.inherit_bg,
            noIndent && st.no_indent,
            isFirstLoading && st.loader_wrap,
            (!publicationId || !unitId) && !IS_WEBCOM && st.not_found,
            (isCourseClosed || (!isPublicCourse && lacksTokenInWC)) && st.not_found,
          )
        }
        id="edt-course-completion"
      >
        <TelegramWarningModal />
        {
          (() => {
            if ((!publicationId || !unitId) && !IS_WEBCOM) {
              return <>Добавьте поля Publication ID и Unit ID</>;
            }

            if (isFirstLoading) {
              return <Loader isFullPage />;
            }

            if (isCourseClosed || (!isPublicCourse && lacksTokenInWC)) {
              return <>Урок недоступен</>;
            }

            return courseData ?
              (
                <SnackbarProvider>
                  <TypographyProvider>
                    <IdProvider
                      publicationId={publicationId}
                      unitId={unitId}
                    >
                      <MainProvider
                        hasSuccessScreen={courseData.hasSuccessScreen}
                        isAutomaticFinishLesson={isAutomaticFinishLesson}
                        isCoursePassed={courseData.status === LessonStatus.Passed || isAllRootElementsFinished}
                        isCoursePassedByBackend={courseData.status === LessonStatus.Passed}
                        isLessonsLoading={isLoading}
                        lessonNodeIds={onlyLessonsList}
                        library={library}
                        rootNodeIds={rootNodeIds}
                        setLibrary={setLibrary}
                        unitsChildren={unitsChildren}
                      >
                        <AnalyticsProvider
                          analyticEventsCache={analyticEventsCache}
                          isAutomaticFinishLesson={isAutomaticFinishLesson}
                        >
                          <EdtechPlayerManager withoutBorderRadius={videoWithoutBorderRadius} />
                          <CourseCompletion
                            backButtonPath={backButtonPath}
                            courseData={courseData}
                            hideRating={hideRating || !courseData?.feedback}
                            progressCompletedLessonsCount={courseProgressCompletedLessonsCount}
                            progressLessonsCount={courseProgressLessonsCount}
                          />
                        </AnalyticsProvider>
                      </MainProvider>
                    </IdProvider>
                  </TypographyProvider>
                </SnackbarProvider>
              ) :
              null;
          })()
        }
      </section>
    </EdtechProvider>
  );
};
